import { useEffect, useState } from 'react';
import { reload } from 'firebase/auth';
import { auth } from '../firebase';

// Is this user's email address confirmed?
//
// `user.emailVerified` comes from the cached ID token, and that token is not
// reissued when someone opens a confirmation link — so a user who just
// confirmed, in a different tab or on their phone, comes back to a page still
// telling them to confirm. The fix is a one-shot reload() of the auth record.
//
// Returns the flag plus a `recheck()` for the moment after we've asked them to
// go and click the link.
export default function useEmailVerified(user) {
  const [verified, setVerified] = useState(() => user?.emailVerified === true);

  // Re-seed when the signed-in user changes, so a sign-out/sign-in doesn't
  // carry the previous account's answer over.
  useEffect(() => {
    setVerified(user?.emailVerified === true);
  }, [user?.uid, user?.emailVerified]);

  const recheck = async () => {
    const current = auth.currentUser;
    if (!current) return false;
    try {
      await reload(current);
      const next = current.emailVerified === true;
      setVerified(next);
      return next;
    } catch {
      // Offline, or the account is gone. Leave the last known answer alone;
      // nothing here gates access, so a stale `false` only shows a prompt.
      return false;
    }
  };

  // One reload per mount, and only while we still think it's unconfirmed —
  // there is nothing to learn once it's true.
  useEffect(() => {
    if (!user || user.emailVerified === true) return;
    let cancelled = false;
    (async () => {
      const current = auth.currentUser;
      if (!current) return;
      try {
        await reload(current);
        if (!cancelled && current.emailVerified === true) setVerified(true);
      } catch { /* see recheck() */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  return [verified, recheck];
}
